// Public, auth-free player for shared narrations.
// Renders chapters + scene-cues + plays through them with the same layered audio
// playback as the in-app player (main TTS + per-chapter scene SFX overlay).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Play, Pause, SkipForward, SkipBack, Square, Sparkles, Mic, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedNarrationPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/share/narrate/${token}`);
        if (alive) { setData(r.data); setLoading(false); }
      } catch (e) {
        if (alive) {
          setError(e.response?.status === 404 ? 'This shared audiobook was removed or revoked.' : 'Could not load this audiobook.');
          setLoading(false);
        }
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) return <Shell><EmptyState>Loading audiobook…</EmptyState></Shell>;
  if (error)   return <Shell><EmptyState icon="🔒" title="Audiobook unavailable" hint="Ask the person who shared this for a fresh link.">{error}</EmptyState></Shell>;
  if (!data)   return <Shell><EmptyState icon="🔍" title="Not found">This audiobook does not exist.</EmptyState></Shell>;

  return <Shell><Player data={data} /></Shell>;
}

function EmptyState({ icon, title, hint, children }) {
  return (
    <div className="rounded-3xl border p-10 text-center"
      style={{ borderColor: 'rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)' }}>
      {icon && <div className="text-5xl mb-3">{icon}</div>}
      {title && <div className="text-lg font-black mb-1.5">{title}</div>}
      <div className="text-sm opacity-80 max-w-sm mx-auto">{children}</div>
      {hint && <div className="text-[12px] opacity-50 mt-3">{hint}</div>}
      <Link to="/login" className="inline-flex items-center gap-1.5 mt-5 rounded-full px-4 py-2 text-xs font-bold"
        style={{ background: 'linear-gradient(135deg,#0EA5E9,#10B981)', color: '#fff' }}>
        Open Desky <ExternalLink className="w-3 h-3" />
      </Link>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen" style={{ background: '#0B1220', color: '#E5E7EB' }}>
      <header className="border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="text-sm font-bold inline-flex items-center gap-1.5">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#0EA5E9,#10B981)' }}>D</span>
            Desky · Super Narrate
          </Link>
          <Link to="/login" className="text-[12px] opacity-80 hover:opacity-100 inline-flex items-center gap-1">
            Make your own <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-8">{children}</main>
    </div>
  );
}

function Player({ data }) {
  const m = data.meta || {};
  const accent = m.genre_accent || '#0EA5E9';
  const gradient = (m.genre_gradient && m.genre_gradient.length >= 2) ? m.genre_gradient : [accent, accent];

  const chapters = useMemo(() => (data.chapters || []).map((ch, i) => ({
    ...ch,
    audio_url: ch.audio_url ? `${process.env.REACT_APP_BACKEND_URL}${ch.audio_url}` : null,
    scene_cues: (ch.scene_cues || []).map(c => ({ ...c, url: c.url ? `${process.env.REACT_APP_BACKEND_URL}${c.url}` : null })),
  })), [data]);

  const [chapterIdx, setChapterIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const audioRef = useRef(null);
  const cueRefs = useRef([]);
  const playedCues = useRef(new Set());

  // Schedule cue overlays as the chapter audio progresses
  const onTimeUpdate = () => {
    const a = audioRef.current; if (!a) return;
    const ctime = a.currentTime || 0;
    const cdur = a.duration || 1;
    setPos(ctime); setDur(cdur);
    const cues = chapters[chapterIdx]?.scene_cues || [];
    cues.forEach((c, j) => {
      const target = c.position === 'intro' ? 0.05 : c.position === 'outro' ? 0.92 : 0.5;
      const t = target * cdur;
      const key = `${chapterIdx}:${j}`;
      if (ctime >= t && !playedCues.current.has(key)) {
        playedCues.current.add(key);
        const el = cueRefs.current[j];
        if (el) { el.volume = 0.55; el.currentTime = 0; el.play().catch(() => {}); }
      }
    });
  };

  const onEnded = () => {
    setPlaying(false); setPaused(false);
    playedCues.current.clear();
    if (chapterIdx < chapters.length - 1) {
      setChapterIdx(chapterIdx + 1);
      setTimeout(() => audioRef.current?.play().catch(() => {}), 80);
    }
  };

  // Reset cue state when chapter changes
  useEffect(() => { playedCues.current = new Set(); }, [chapterIdx]);

  const play = () => audioRef.current?.play().then(() => { setPlaying(true); setPaused(false); }).catch(() => {});
  const pauseFn = () => { audioRef.current?.pause(); setPaused(true); setPlaying(false); };
  const stopFn = () => { const a = audioRef.current; if (a) { a.pause(); a.currentTime = 0; } setPlaying(false); setPaused(false); };
  const next = () => { setChapterIdx(Math.min(chapters.length - 1, chapterIdx + 1)); setTimeout(() => audioRef.current?.play().catch(() => {}), 100); };
  const prev = () => { setChapterIdx(Math.max(0, chapterIdx - 1)); setTimeout(() => audioRef.current?.play().catch(() => {}), 100); };

  const seekTo = (e) => {
    const a = audioRef.current; if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * dur;
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const mm = Math.floor(s / 60); const ss = Math.floor(s % 60);
    return `${mm}:${String(ss).padStart(2, '0')}`;
  };

  const ch = chapters[chapterIdx] || {};
  const totalCh = chapters.length || 1;
  const overallProgress = ((chapterIdx + (dur > 0 ? pos / dur : 0)) / totalCh) * 100;

  return (
    <div className="space-y-6">
      <div className="rounded-3xl p-6 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`, color: 'white' }}>
        <div className="pointer-events-none absolute inset-0 opacity-20 mix-blend-overlay"
          style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '3px 3px' }} />
        <div className="relative">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest opacity-90">
            <Mic className="w-3.5 h-3.5" /> Super Narrate
            {m.genre_label && <span>· {m.genre_emoji} {m.genre_label}</span>}
            {m.language_native && <span>· {m.language_native}</span>}
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight mt-2 drop-shadow" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>{data.title}</h1>
          <p className="text-sm mt-2 opacity-95 max-w-xl">{data.preview}</p>
        </div>
      </div>

      {/* Audio elements */}
      {ch.audio_url && (
        <audio ref={audioRef} src={ch.audio_url} onTimeUpdate={onTimeUpdate} onLoadedMetadata={onTimeUpdate}
          onEnded={onEnded} preload="auto" />
      )}
      {(ch.scene_cues || []).map((c, j) => c.url && (
        <audio key={`cue-${chapterIdx}-${j}`} ref={el => cueRefs.current[j] = el} src={c.url} preload="auto" />
      ))}

      <div className="rounded-2xl p-5 space-y-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={prev} disabled={chapterIdx === 0} className="w-10 h-10 rounded-full border flex items-center justify-center disabled:opacity-30" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
            <SkipBack className="w-4 h-4" />
          </button>
          {playing && !paused ? (
            <button onClick={pauseFn} className="w-14 h-14 rounded-full text-white flex items-center justify-center" style={{ background: accent }}>
              <Pause className="w-5 h-5" />
            </button>
          ) : (
            <button onClick={play} disabled={!ch.audio_url} className="w-14 h-14 rounded-full text-white flex items-center justify-center disabled:opacity-50" style={{ background: accent }}>
              <Play className="w-5 h-5 ml-0.5" />
            </button>
          )}
          <button onClick={stopFn} disabled={!playing && !paused} className="w-10 h-10 rounded-full border flex items-center justify-center disabled:opacity-30" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
            <Square className="w-3.5 h-3.5" />
          </button>
          <button onClick={next} disabled={chapterIdx >= chapters.length - 1} className="w-10 h-10 rounded-full border flex items-center justify-center disabled:opacity-30" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
            <SkipForward className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-[180px]">
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-70 flex items-center justify-between">
              <span>Chapter {chapterIdx + 1} / {chapters.length}</span>
              <span className="font-mono">{fmt(pos)} / {fmt(dur)}</span>
            </div>
            <div onClick={seekTo} className="h-2 rounded-full mt-1.5 overflow-hidden cursor-pointer" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <div className="h-full transition-[width] duration-200" style={{ width: `${overallProgress}%`, background: `linear-gradient(90deg, ${gradient[0]}, ${gradient[1]})` }} />
            </div>
            <div className="text-[12.5px] font-semibold mt-1.5 truncate">{ch.title || '—'}</div>
          </div>
        </div>
        <div className="text-[10.5px] opacity-60 inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> {m.voice_name || 'ElevenLabs voice'}
        </div>
      </div>

      <div className="rounded-2xl divide-y" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {chapters.map((c, i) => (
          <button key={i} onClick={() => { setChapterIdx(i); setTimeout(() => audioRef.current?.play().catch(() => {}), 100); }}
            className="w-full text-left px-4 py-3 transition-colors"
            style={{ background: i === chapterIdx ? `color-mix(in srgb, ${accent} 14%, transparent)` : 'transparent' }}>
            <div className="text-[12.5px] font-bold inline-flex items-center gap-2">
              <span className="w-5 h-5 rounded-full text-[10px] font-bold text-white flex items-center justify-center shrink-0" style={{ background: accent }}>{i+1}</span>
              {c.title}
            </div>
            <p className="text-[12.5px] mt-1.5 leading-relaxed line-clamp-3 opacity-80">{c.narration}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
